import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportClientError } from '../lib/glitchtip';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level React error boundary.
 *
 * Exists for two reasons: a render crash shows a recoverable screen instead of a
 * blank page, and — importantly — the error gets reported. React swallows render
 * errors, so they never reach `window.onerror` and the Sentry SDK's global
 * handlers cannot see them; a boundary is the only way to observe them.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(error, {
      source: 'react-error-boundary',
      component_stack: info.componentStack,
    });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-lg font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          The page hit an unexpected error. It has been reported. You can try again, or
          reload if the problem persists.
        </p>
        <pre className="max-w-lg overflow-x-auto rounded bg-muted p-3 text-left text-xs">
          {error.message}
        </pre>
        <div className="flex gap-2">
          <button
            className="rounded border px-3 py-1.5 text-sm"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
          <button
            className="rounded border px-3 py-1.5 text-sm"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
